import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Place as PlaceIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks';

const StatCard = ({
  title,
  icon,
  color,
}: {
  title: string;
  icon: React.ReactNode;
  color: string;
}) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'visible',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
      borderRadius: 2,
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
      },
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: alpha(color, 0.1),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        바로가기 &rarr;
      </Typography>
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();

  const cards = [
    {
      title: '키워드 관리',
      icon: <SearchIcon fontSize="medium" />,
      color: theme.palette.primary.main,
    },
    {
      title: '순위 체크',
      icon: <TrendingUpIcon fontSize="medium" />,
      color: theme.palette.success.main,
    },
    {
      title: '플레이스 분석',
      icon: <PlaceIcon fontSize="medium" />,
      color: theme.palette.warning.main,
    },
    {
      title: '리포트',
      icon: <AssessmentIcon fontSize="medium" />,
      color: theme.palette.info.main,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          안녕하세요, {user?.name}님!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          오늘의 광고 성과를 확인해보세요.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>
      
      {/* Placeholder for charts or tables */}
      <Box sx={{ mt: 4 }}>
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            최근 활동
          </Typography>
          <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', borderRadius: 1 }}>
            <Typography color="text.secondary">
              데이터를 불러오는 중입니다...
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default DashboardPage;
